import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import MobileLayout from "../components/layout/MobileLayout";
import { ArrowLeft, History, ChevronRight, Wallet } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";

/* =========================================================================
   DRAGON · TIGER — Imperial Jade × Antique Gold Duel Arena.
   Cards emerge between two flame pillars. History runs right→left (latest ◀).
   Winner reveal fires a gold burst celebration.
   ========================================================================= */

const CHOICES = [
  { key: "dragon", label: "DRAGON", short: "D",   payout: "2×",  accent: "#3b82f6", accent2: "#1e3a8a", glow: "rgba(59,130,246,0.75)",  emoji: "🐲" },
  { key: "tie",    label: "TIE",    short: "Tie", payout: "10×", accent: "#f4d68a", accent2: "#8a5a12", glow: "rgba(244,214,138,0.85)", emoji: "⚔" },
  { key: "tiger",  label: "TIGER",  short: "T",   payout: "2×",  accent: "#dc2626", accent2: "#7f1d1d", glow: "rgba(220,38,38,0.75)",   emoji: "🐯" },
];

const PRESETS = [50, 100, 500, 1000, 5000];
const SPIN_DURATION_MS = 2800; // sync cards + celebration timing

const SUIT_MAP = {
  H: { symbol: "♥", color: "#dc2626" },
  D: { symbol: "♦", color: "#dc2626" },
  S: { symbol: "♠", color: "#0f172a" },
  C: { symbol: "♣", color: "#0f172a" },
};
const RANK_MAP = { 11: "J", 12: "Q", 13: "K", 14: "A" };
const rankLabel = (r) => (RANK_MAP[r] || String(r));

export default function DragonTigerGamePage() {
  const { user, refreshUser } = useAuth();
  const [round, setRound] = useState(null);
  const [amount, setAmount] = useState(50);
  const [placing, setPlacing] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [landed, setLanded] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const previousRoundIdRef = useRef(null);
  const revealStartedForRoundRef = useRef(null);
  const audioRef = useRef(null);

  const fetchRound = useCallback(async () => {
    try {
      const { data } = await api.get(`/games/dragon_tiger/current`);
      setRound(data);
      if (previousRoundIdRef.current && previousRoundIdRef.current !== data.round_id) {
        setLanded(false);
        setShowCelebration(false);
        revealStartedForRoundRef.current = null;
        refreshUser();
      }
      if (!data.betting_open && data.result && revealStartedForRoundRef.current !== data.round_id) {
        revealStartedForRoundRef.current = data.round_id;
        setLanded(false);
        setShowCelebration(false);
        try { audioRef.current?.play?.().catch(() => {}); } catch (_) {}
        setTimeout(() => { try { audioRef.current?.play?.().catch(() => {}); } catch(_){} }, 1400);
        setTimeout(() => {
          setLanded(true);
          setShowCelebration(true);
        }, SPIN_DURATION_MS);
      }
      previousRoundIdRef.current = data.round_id;
    } catch (e) { console.error(e); }
  }, [refreshUser]);

  useEffect(() => {
    fetchRound();
    const id = setInterval(fetchRound, 1000);
    return () => clearInterval(id);
  }, [fetchRound]);

  const placeBet = async (choice) => {
    if (!round?.betting_open) return toast.error("Betting is locked");
    if (amount < (round?.min_bet || 50)) return toast.error(`Min bet ₹${round?.min_bet || 50}`);
    if ((user?.wallet_balance ?? 0) < amount) return toast.error("Insufficient wallet balance");
    setPlacing(true);
    try {
      await api.post(`/games/dragon_tiger/bet`, { choice, amount });
      toast.success(`₹${amount} on ${choice.toUpperCase()} placed!`);
      refreshUser();
      fetchRound();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPlacing(false); }
  };

  const myBetsByChoice = useMemo(() => {
    const map = { dragon: 0, tie: 0, tiger: 0 };
    (round?.my_bets || []).forEach(b => { map[b.choice] = (map[b.choice] || 0) + (b.amount || 0); });
    return map;
  }, [round]);

  if (!round) return (
    <MobileLayout>
      <div className="h-64 flex items-center justify-center text-amber-200/70 tracking-widest text-xs">LOADING…</div>
    </MobileLayout>
  );

  const bettingRemainingSec = round.betting_time_left != null
    ? round.betting_time_left
    : Math.max(0, round.time_left - (round.round_duration - round.betting_window));
  const inReveal = !round.betting_open;
  const currentResult = inReveal ? round.result : null;
  const winnerResult = landed ? currentResult : null;
  const timerSec = Math.max(0, Math.ceil(bettingRemainingSec));
  const timerFraction = Math.max(0, Math.min(1, bettingRemainingSec / round.betting_window));
  const criticalTime = round.betting_open && bettingRemainingSec <= 5;
  const revealCards = inReveal
    ? { dragon_card: round.dragon_card, tiger_card: round.tiger_card }
    : { dragon_card: null, tiger_card: null };

  return (
    <MobileLayout>
      <Toaster richColors position="top-center" />
      <audio ref={audioRef} src="/games/card-flip.mp3" preload="auto" />

      {/* IMPERIAL JADE BACKGROUND */}
      <div
        className="absolute left-0 right-0 top-16 bottom-0 -z-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 25%, #0f2a25 0%, #061512 55%, #020806 100%)",
        }}
      />
      {/* dragon-scale diagonal pattern */}
      <div
        className="absolute left-0 right-0 top-16 bottom-0 -z-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(60deg, rgba(212,169,74,0.5) 0 1px, transparent 1px 12px), repeating-linear-gradient(-60deg, rgba(212,169,74,0.5) 0 1px, transparent 1px 12px)",
        }}
      />

      <div className="relative z-10">
        {/* GOLD-FRAMED HEADER */}
        <div className="sticky top-0 z-30 -mx-3 px-3 py-2 mb-3 backdrop-blur-2xl bg-black/40 border-b border-amber-300/25 flex items-center justify-between">
          <Link to="/" data-testid="back-btn" className="w-8 h-8 rounded-full bg-white/5 border border-amber-300/30 flex items-center justify-center text-amber-200 active:scale-95 transition-transform">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex flex-col items-center leading-none">
            <div className="flex items-center gap-2">
              <span className="text-[13px]" style={{ color: "#3b82f6" }}>🐲</span>
              <span
                className="font-display font-black tracking-[0.3em] text-[11px]"
                style={{ color: "#d4a94a", textShadow: "0 0 12px rgba(212,169,74,0.4)" }}
              >
                DRAGON · TIGER
              </span>
              <span className="text-[13px]" style={{ color: "#dc2626" }}>🐯</span>
            </div>
            <span className="text-[8px] tracking-[0.4em] text-amber-200/40 mt-0.5">ROUND #{round.round_number}</span>
          </div>
          <div
            data-testid="wallet-chip"
            className="flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-amber-300/30 px-2.5 py-1 backdrop-blur-md"
          >
            <Wallet className="w-3 h-3 text-amber-200" />
            <span className="tabular-nums text-[11px] font-black" style={{ color: "#f4d68a" }}>₹{user?.wallet_balance ?? 0}</span>
          </div>
        </div>

        {/* IMPERIAL TIMER RIBBON */}
        <div
          data-testid="round-card"
          className="relative mb-3 mx-auto max-w-[300px] rounded-xl px-3 py-2 flex items-center gap-3 shadow-xl overflow-hidden"
          style={{
            background: "linear-gradient(90deg, rgba(59,130,246,0.15) 0%, rgba(15,42,37,0.85) 40%, rgba(15,42,37,0.85) 60%, rgba(220,38,38,0.15) 100%)",
            border: "1px solid rgba(212,169,74,0.35)",
          }}
        >
          {/* dragon flame emblem */}
          <div className="text-lg leading-none">🐲</div>
          <div className="flex-1 flex flex-col items-center">
            {round.betting_open ? (
              <>
                <div className="text-[8px] tracking-[0.35em] font-black text-amber-200/70">BET ENDS IN</div>
                <div
                  data-testid="countdown"
                  className={`font-display font-black tabular-nums leading-none ${criticalTime ? "animate-pulse" : ""}`}
                  style={{
                    fontSize: "22px",
                    color: criticalTime ? "#fda4af" : "#f4d68a",
                    textShadow: criticalTime ? "0 0 14px rgba(251,113,133,0.65)" : "0 0 14px rgba(244,214,138,0.5)",
                  }}
                >
                  {timerSec}<span className="text-xs opacity-70">s</span>
                </div>
              </>
            ) : (
              <>
                <div className="text-[8px] tracking-[0.35em] font-black text-amber-200/70">
                  {landed ? "RESULT" : "REVEALING"}
                </div>
                <div
                  data-testid="countdown"
                  className="font-display font-black tabular-nums leading-none text-amber-100 animate-pulse tracking-[0.3em]"
                  style={{ fontSize: "14px" }}
                >
                  ▪ ▪ ▪
                </div>
              </>
            )}
            {/* Hairline gold progress under timer */}
            <div className="mt-1 h-[2px] w-24 rounded-full bg-black/40 overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-700 ease-linear"
                style={{
                  width: round.betting_open ? `${timerFraction * 100}%` : "0%",
                  background: criticalTime
                    ? "linear-gradient(90deg, #fb923c, #facc15)"
                    : "linear-gradient(90deg, #d4a94a, #fef3c7, #d4a94a)",
                }}
              />
            </div>
          </div>
          {/* tiger flame emblem */}
          <div className="text-lg leading-none">🐯</div>
        </div>

        {/* DUEL ARENA — cards between two flame pillars */}
        <div
          className="relative mb-3 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
          style={{
            background: "radial-gradient(ellipse at 50% 45%, #10382e 0%, #061a15 65%, #020806 100%)",
            border: "1.5px solid rgba(212,169,74,0.35)",
          }}
        >
          {/* Inner gold frame with corner ornaments */}
          <div className="absolute inset-2 rounded-[14px] border border-amber-300/10 pointer-events-none" />
          {["tl", "tr", "bl", "br"].map((pos) => (
            <span
              key={pos}
              className="absolute w-3 h-3 pointer-events-none"
              style={{
                [pos.includes("t") ? "top" : "bottom"]: "10px",
                [pos.includes("l") ? "left" : "right"]: "10px",
                borderTop: pos.includes("t") ? "1.5px solid #d4a94a" : "none",
                borderBottom: pos.includes("b") ? "1.5px solid #d4a94a" : "none",
                borderLeft: pos.includes("l") ? "1.5px solid #d4a94a" : "none",
                borderRight: pos.includes("r") ? "1.5px solid #d4a94a" : "none",
              }}
            />
          ))}

          {/* Ambient flame glows */}
          <div className="absolute -left-4 top-6 w-28 h-28 rounded-full bg-blue-500/25 blur-3xl pointer-events-none animate-pulse" />
          <div className="absolute -right-4 top-6 w-28 h-28 rounded-full bg-rose-500/25 blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: "1s" }} />

          {/* Side pillar labels */}
          <div className="relative flex items-center justify-between px-4 pt-3 pb-1">
            <div className="flex items-center gap-1.5">
              <div className="text-xl leading-none" style={{ filter: landed && currentResult === "dragon" ? "drop-shadow(0 0 12px rgba(59,130,246,0.9))" : "none" }}>🐲</div>
              <div className="text-[9px] font-black tracking-[0.3em]" style={{ color: "#93c5fd" }}>DRAGON</div>
            </div>
            <div className="font-display font-black tracking-[0.4em] text-[10px]" style={{ color: "#d4a94a" }}>VS</div>
            <div className="flex items-center gap-1.5">
              <div className="text-[9px] font-black tracking-[0.3em]" style={{ color: "#fca5a5" }}>TIGER</div>
              <div className="text-xl leading-none" style={{ filter: landed && currentResult === "tiger" ? "drop-shadow(0 0 12px rgba(220,38,38,0.9))" : "none" }}>🐯</div>
            </div>
          </div>

          {/* Cards row */}
          <div className="relative flex items-center justify-center gap-5 py-3">
            <LuxuryCard
              card={revealCards.dragon_card}
              flipped={inReveal && !!revealCards.dragon_card}
              landed={landed && (currentResult === "dragon" || currentResult === "tie")}
              flipDelay={0}
              side="dragon"
            />
            {/* Ornate gold center divider */}
            <div className="absolute inset-y-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5">
              <div className="w-px flex-1 bg-gradient-to-b from-transparent via-amber-300/50 to-transparent" />
              <div className="text-[10px]" style={{ color: "#d4a94a" }}>◈</div>
              <div className="w-px flex-1 bg-gradient-to-b from-transparent via-amber-300/50 to-transparent" />
            </div>
            <LuxuryCard
              card={revealCards.tiger_card}
              flipped={inReveal && !!revealCards.tiger_card}
              landed={landed && (currentResult === "tiger" || currentResult === "tie")}
              flipDelay={1200}
              side="tiger"
            />
          </div>

          {/* Result badge / status */}
          <div className="relative pb-3 flex justify-center">
            {landed && winnerResult ? (
              (() => {
                const c = CHOICES.find(x => x.key === winnerResult);
                return (
                  <div
                    className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border font-display font-black text-[10px] tracking-[0.25em] backdrop-blur-md animate-pulse"
                    style={{
                      background: `linear-gradient(135deg, ${c.accent}30, ${c.accent2}30)`,
                      borderColor: `${c.accent}`,
                      color: c.accent === "#f4d68a" ? "#fef3c7" : "#fff",
                      boxShadow: `0 0 26px ${c.glow}`,
                    }}
                  >
                    🎉 {c.label} WON · {c.payout} 🎉
                  </div>
                );
              })()
            ) : inReveal ? (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-400/50 text-rose-100 text-[9px] font-black tracking-[0.25em] backdrop-blur-md animate-pulse">
                <span className="w-1 h-1 rounded-full bg-rose-300" /> REVEALING CARDS
              </div>
            ) : (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-amber-100 text-[9px] font-black tracking-[0.25em] backdrop-blur-md"
                style={{ background: "rgba(212,169,74,0.12)", border: "1px solid rgba(212,169,74,0.45)" }}
              >
                PLACE YOUR BET
              </div>
            )}
          </div>

          {/* Celebration burst */}
          {showCelebration && currentResult && (
            <CelebrationBurst result={currentResult} />
          )}
        </div>

        {/* BETTING BANNERS — 3 painted scroll-style tiles */}
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="choice-grid">
          {CHOICES.map((c) => {
            const myBet = myBetsByChoice[c.key];
            const isWinner = landed && winnerResult === c.key;
            const disabled = !round.betting_open || placing;
            return (
              <BannerSpot
                key={c.key}
                choice={c}
                myBet={myBet}
                isWinner={isWinner}
                disabled={disabled}
                onPlace={() => placeBet(c.key)}
              />
            );
          })}
        </div>

        {/* BET AMOUNT — imperial glass */}
        <div
          data-testid="amount-selector"
          className="rounded-xl p-2.5 mb-3"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))",
            border: "1px solid rgba(212,169,74,0.25)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-black text-[13px]"
              style={{
                background: "radial-gradient(circle at 30% 30%, #f4d68a, #8a5a12)",
                color: "#3d2005",
                boxShadow: "inset 0 -1px 3px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.4)",
              }}
            >
              ₹
            </div>
            <input
              type="number"
              min={round.min_bet}
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
              data-testid="amount-input"
              className="flex-1 min-w-0 text-lg font-black text-amber-100 tabular-nums bg-transparent outline-none placeholder:text-white/30"
            />
            <div className="text-[9px] text-amber-200/40 tracking-[0.2em] shrink-0">MIN ₹{round.min_bet}</div>
          </div>
          <div className="grid grid-cols-5 gap-1">
            {PRESETS.map(v => {
              const active = amount === v;
              return (
                <button
                  key={v}
                  onClick={() => setAmount(v)}
                  data-testid={`preset-${v}`}
                  className={`h-8 rounded-lg font-black text-[11px] tabular-nums transition-transform active:scale-90`}
                  style={{
                    background: active ? "linear-gradient(135deg, #f4d68a, #b57d1e)" : "rgba(255,255,255,0.03)",
                    border: active ? "1px solid #f4d68a" : "1px solid rgba(212,169,74,0.2)",
                    color: active ? "#3d2005" : "#f4d68a",
                    boxShadow: active ? "0 0 10px rgba(244,214,138,0.5)" : "none",
                  }}
                >
                  {v >= 1000 ? `${v/1000}K` : v}
                </button>
              );
            })}
          </div>
        </div>

        {/* HISTORY — imperial coin tokens, latest ◀ */}
        <div
          data-testid="history-strip"
          className="rounded-xl p-2.5 mb-3"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))",
            border: "1px solid rgba(212,169,74,0.25)",
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.3em] text-amber-200/70 font-black">
              <History className="w-3 h-3" /> HISTORY
            </div>
            <button
              onClick={() => setShowHistoryModal(true)}
              data-testid="game-history-btn"
              className="flex items-center gap-0.5 text-[9px] font-black tracking-wider text-amber-200/80"
            >
              ALL <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="shrink-0 text-[8px] font-black tracking-[0.2em] px-1.5 py-1 rounded flex flex-col items-center leading-none"
              style={{
                background: "linear-gradient(180deg, rgba(212,169,74,0.25), rgba(212,169,74,0.05))",
                border: "1px solid rgba(212,169,74,0.4)",
                color: "#f4d68a",
              }}
            >
              LATEST<br/><span className="text-[10px] mt-0.5">◀</span>
            </div>
            <div className="flex-1 flex gap-1.5 overflow-x-auto pb-0.5">
              {round.history?.map((h, i) => {
                const c = CHOICES.find(x => x.key === h.result) || CHOICES[0];
                const isLatest = i === 0;
                return (
                  <div
                    key={i}
                    data-testid={`history-${i}`}
                    className="shrink-0 rounded-full flex items-center justify-center font-black"
                    style={{
                      width: 28,
                      height: 28,
                      background: `radial-gradient(circle at 30% 30%, ${c.accent}, ${c.accent2})`,
                      border: isLatest ? "1.5px solid #f4d68a" : `1px solid ${c.accent2}`,
                      color: "#fff",
                      fontSize: c.short === "Tie" ? "8px" : "12px",
                      boxShadow: isLatest
                        ? `0 0 12px ${c.glow}, inset 0 -1px 3px rgba(0,0,0,0.5)`
                        : `inset 0 -1px 3px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.25)`,
                    }}
                    title={`#${h.round_number} · ${c.label}`}
                  >
                    {c.short}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {showHistoryModal && (
          <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-end animate-fade-in" onClick={() => setShowHistoryModal(false)}>
            <div className="w-full max-w-md mx-auto bg-gradient-to-b from-[#0f2a25] to-black border-t-2 border-amber-300/60 rounded-t-3xl p-4 max-h-[75vh] overflow-y-auto pb-24" onClick={e => e.stopPropagation()}>
              <div className="text-center mb-4">
                <div className="font-display font-black text-xl tracking-[0.2em]" style={{ color: "#d4a94a" }}>DUEL HISTORY</div>
                <div className="text-[10px] tracking-widest text-amber-200/50 mt-1">Latest first</div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {round.history?.map((h, i) => {
                  const c = CHOICES.find(x => x.key === h.result) || CHOICES[0];
                  return (
                    <div key={i} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 border border-amber-300/15">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center border-2 text-white font-black text-sm"
                        style={{
                          background: `radial-gradient(circle at 30% 30%, ${c.accent}, ${c.accent2})`,
                          borderColor: c.accent,
                        }}
                      >
                        {c.short}
                      </div>
                      <div className="text-[9px] font-black text-amber-200 tracking-wider">#{h.round_number}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

/* -------------------------------------------------------------------------
   LuxuryCard — compact playing card with imperial gold border and diamond back
   ------------------------------------------------------------------------- */
function LuxuryCard({ card, flipped, landed, flipDelay = 0, side }) {
  const suit = card ? SUIT_MAP[card.suit] : null;
  const rank = card ? rankLabel(card.rank) : "";
  const style = {
    transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
    transition: `transform 800ms cubic-bezier(0.175, 0.885, 0.32, 1.275) ${flipDelay}ms`,
  };
  const tilt = side === "dragon" ? "-4deg" : "4deg";
  return (
    <div
      className="relative w-[76px] aspect-[2/3] [perspective:1200px]"
      style={{
        transform: `rotate(${tilt}) ${landed ? "translateY(-6px) scale(1.08)" : ""}`,
        transition: "transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    >
      <div className="absolute -bottom-2 inset-x-2 h-2 rounded-full bg-black/70 blur-md" />
      <div className="absolute inset-0 [transform-style:preserve-3d]" style={style}>
        {/* BACK */}
        <div
          className="absolute inset-0 rounded-lg [backface-visibility:hidden] overflow-hidden shadow-[0_8px_20px_rgba(0,0,0,0.7)]"
          style={{
            background: "linear-gradient(135deg, #12403a 0%, #061a15 100%)",
            border: landed ? "1.5px solid #fef3c7" : "1px solid rgba(212,169,74,0.5)",
          }}
        >
          <div
            className="absolute inset-1.5 rounded-md opacity-40"
            style={{
              backgroundImage:
                "linear-gradient(45deg, rgba(212,169,74,0.5) 25%, transparent 25%, transparent 75%, rgba(212,169,74,0.5) 75%), linear-gradient(45deg, rgba(212,169,74,0.5) 25%, transparent 25%, transparent 75%, rgba(212,169,74,0.5) 75%)",
              backgroundSize: "10px 10px",
              backgroundPosition: "0 0, 5px 5px",
            }}
          />
          <div className="absolute inset-1.5 rounded-md border border-amber-300/30" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-lg"
              style={{
                background: "radial-gradient(circle at 30% 30%, #f4d68a, #8a5a12)",
                color: "#3d2005",
                border: "1.5px solid #d4a94a",
                boxShadow: "inset 0 -1px 3px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.35)",
              }}
            >
              ◈
            </div>
          </div>
        </div>
        {/* FRONT */}
        <div
          className="absolute inset-0 rounded-lg bg-white [backface-visibility:hidden] overflow-hidden shadow-[0_8px_20px_rgba(0,0,0,0.7)]"
          style={{
            transform: "rotateY(180deg)",
            border: landed ? "1.5px solid #fef3c7" : "1.5px solid #d4a94a",
            boxShadow: landed ? "0 0 24px rgba(244,214,138,0.85)" : undefined,
          }}
        >
          {card && suit && (
            <>
              <div className="absolute top-1 left-1.5 font-display font-black leading-none" style={{ color: suit.color }}>
                <div className="text-sm">{rank}</div>
                <div className="text-[10px] leading-none">{suit.symbol}</div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center font-display font-black" style={{ color: suit.color, fontSize: "36px", lineHeight: 1 }}>
                {suit.symbol}
              </div>
              <div className="absolute bottom-1 right-1.5 font-display font-black leading-none text-right rotate-180" style={{ color: suit.color }}>
                <div className="text-sm">{rank}</div>
                <div className="text-[10px] leading-none">{suit.symbol}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   BannerSpot — vertical painted-scroll banner style. Solid gradient with
   ornate gold border, big emoji + label, only shows user's own bet ₹X.
   ------------------------------------------------------------------------- */
function BannerSpot({ choice, myBet, isWinner, disabled, onPlace }) {
  const hasBet = myBet > 0;
  return (
    <button
      onClick={onPlace}
      disabled={disabled}
      data-testid={`choice-${choice.key}`}
      className="relative rounded-xl h-[104px] px-2 py-2 flex flex-col items-center justify-between transition-transform active:scale-95 disabled:active:scale-100 overflow-hidden text-white shadow-lg"
      style={{
        background: `linear-gradient(180deg, ${choice.accent} 0%, ${choice.accent2} 100%)`,
        border: `1.5px solid ${isWinner ? "#fef3c7" : "rgba(212,169,74,0.55)"}`,
        boxShadow: isWinner
          ? `0 0 30px ${choice.glow}, inset 0 0 20px rgba(255,255,255,0.2)`
          : `0 8px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -3px 6px rgba(0,0,0,0.35)`,
        opacity: disabled && !hasBet ? 0.55 : 1,
      }}
    >
      {/* Glossy sheen */}
      <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent pointer-events-none rounded-t-xl" />
      {/* Gold hairline inner frame */}
      <span className="absolute inset-1.5 rounded-lg border border-amber-300/40 pointer-events-none" />
      {/* Payout badge */}
      <div
        className="absolute top-1 right-1 text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded-full"
        style={{
          background: "rgba(0,0,0,0.55)",
          border: "1px solid rgba(212,169,74,0.6)",
          color: "#f4d68a",
        }}
      >
        {choice.payout}
      </div>

      <div className="relative text-4xl leading-none mt-2" style={{ filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.5))" }}>
        {choice.emoji}
      </div>
      <div className="relative font-display font-black text-[13px] tracking-[0.2em] drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
        {choice.label}
      </div>
      {hasBet && (
        <div
          data-testid={`my-bet-${choice.key}`}
          className="relative text-[11px] font-black tracking-wider bg-emerald-500 text-white rounded-full px-2.5 py-0.5 shadow-md border border-white/40"
        >
          ₹{myBet}
        </div>
      )}
    </button>
  );
}

/* -------------------------------------------------------------------------
   CelebrationBurst — gold particle burst + WIN flash, fires on landing
   ------------------------------------------------------------------------- */
function CelebrationBurst({ result }) {
  const PARTICLES = 22;
  const c = CHOICES.find(x => x.key === result) || CHOICES[0];
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-visible" style={{ zIndex: 20 }}>
      <div
        className="absolute rounded-full"
        style={{
          width: 320, height: 320,
          background: `radial-gradient(circle, ${c.accent}30 0%, transparent 55%)`,
          animation: "dtBurstFade 1200ms ease-out forwards",
        }}
      />
      {Array.from({ length: PARTICLES }).map((_, i) => {
        const angle = (i / PARTICLES) * 360;
        const dist = 90 + (i % 3) * 25;
        const size = 6 + (i % 3) * 2;
        const rad = (angle * Math.PI) / 180;
        const dx = Math.cos(rad) * dist;
        const dy = Math.sin(rad) * dist;
        return (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              width: size,
              height: size,
              background: `linear-gradient(135deg, ${c.accent}, ${c.accent2})`,
              boxShadow: `0 0 8px ${c.glow}`,
              animation: `dtParticleFly 1000ms cubic-bezier(0.15, 0.85, 0.35, 1) forwards`,
              ["--dx"]: `${dx}px`,
              ["--dy"]: `${dy}px`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes dtParticleFly {
          0%   { transform: translate(0, 0) scale(0.4); opacity: 0; }
          20%  { transform: translate(calc(var(--dx) * 0.25), calc(var(--dy) * 0.25)) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0; }
        }
        @keyframes dtBurstFade {
          0%   { transform: scale(0.4); opacity: 0.9; }
          100% { transform: scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
